import React from 'react';
import PropTypes from 'prop-types';
import { Drawer, Box, Typography, IconButton, Divider, CircularProgress } from '@mui/material';
import { Iconify } from 'src/components';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { fetchMessageInfo } from 'src/redux/slices/whatsappV2';
import { COLORS } from './styles';

// Track C / F8 — Message Info drawer.
//
// Right-anchored MUI Drawer that lazy-fetches the per-status timestamps for
// a single message via GET .../info/. Shows the standard 4-row WA timeline:
//   Sent      ✓
//   Delivered ✓✓
//   Read      ✓✓ blue
//   Failed    error
// Rows render greyed out when the timestamp is null (e.g. the patient has
// muted notifications and Read never arrives).
//
// Trigger: Info MenuItem in ChatPage's MessageActionSheet (visible on every
// message). Closes via the X icon, ESC, or backdrop click.

const READ_BLUE = '#34B7F1';
const ERROR_RED = '#FF5630';

const formatTimestamp = (iso) => {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return null;
        // Match the chat header date style — locale-aware, 24h time.
        return d.toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch (e) {
        return null;
    }
};

const TimelineRow = ({ icon, color, label, timestamp = null }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            py: 1.25,
            opacity: timestamp ? 1 : 0.45,
        }}
        role="listitem"
    >
        <Box sx={{ pt: 0.25 }}>
            <Iconify icon={icon} sx={{ width: 20, height: 20, color }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: COLORS.textPrimary }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: 12, color: COLORS.textSecondary, mt: 0.25 }}>
                {timestamp || 'Not yet'}
            </Typography>
        </Box>
    </Box>
);

TimelineRow.propTypes = {
    icon: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    timestamp: PropTypes.string,
};

const MessageInfoDrawer = ({ open, onClose, threadId = null, messageId = null }) => {
    const dispatch = useDispatch();

    const { info, loading } = useSelector(
        (state) => ({
            info: state?.whatsappV2?.messageInfo?.[messageId] ?? null,
            loading: Boolean(state?.whatsappV2?.messageInfoLoading?.[messageId]),
        }),
        shallowEqual,
    );

    React.useEffect(() => {
        if (open && threadId && messageId) {
            dispatch(fetchMessageInfo(threadId, messageId));
        }
    }, [open, threadId, messageId, dispatch]);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100vw', sm: 360 },
                    backgroundColor: COLORS.paper,
                },
            }}
            ModalProps={{ keepMounted: false }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    borderBottom: `1px solid ${COLORS.divider}`,
                }}
            >
                <Typography sx={{ fontSize: 16, fontWeight: 600, color: COLORS.textPrimary }}>
                    Message info
                </Typography>
                <IconButton
                    aria-label="Close message info"
                    onClick={onClose}
                    size="small"
                >
                    <Iconify icon="mdi:close" width={20} height={20} />
                </IconButton>
            </Box>

            <Box sx={{ p: 2 }}>
                {loading && !info ? (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 4,
                        }}
                    >
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <Box role="list" aria-label="Message status timeline">
                        <TimelineRow
                            icon="charm:tick"
                            color={COLORS.textSecondary}
                            label="Sent"
                            timestamp={formatTimestamp(info?.sent_at)}
                        />
                        <Divider />
                        <TimelineRow
                            icon="charm:tick-double"
                            color={COLORS.textSecondary}
                            label="Delivered"
                            timestamp={formatTimestamp(info?.delivered_at)}
                        />
                        <Divider />
                        <TimelineRow
                            icon="charm:tick-double"
                            color={READ_BLUE}
                            label="Read"
                            timestamp={formatTimestamp(info?.read_at)}
                        />
                        {info?.failed_at && (
                            <>
                                <Divider />
                                <TimelineRow
                                    icon="mdi:alert-circle"
                                    color={ERROR_RED}
                                    label="Failed"
                                    timestamp={formatTimestamp(info.failed_at)}
                                />
                            </>
                        )}
                    </Box>
                )}
            </Box>
        </Drawer>
    );
};

MessageInfoDrawer.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    threadId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    messageId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default MessageInfoDrawer;
